import React, { useEffect } from 'react';
import logo from '../assets/logo.png';
import mascot from '../assets/mascot.png';
import home1 from '../assets/home1.png';
import home2 from '../assets/home2.png';
import home3 from '../assets/home3.png';

export default function Home({ user }) {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <main>
        {/* HERO */}
        <section className="hero-wrap">
          <div className="container">
            <div className="home-hero card reveal">
              <div className="hero-left">
                <div className="hero-brand">
                  <img src={logo} alt="Ban Appeals logo" className="hero-logo" />
                  <div>
                    <span className="hero-pill">Ban Appeals</span>
                    <h1 className="hero-title">
                      The easiest way to manage ban appeals on Discord
                    </h1>
                  </div>
                </div>

                <p className="hero-subtitle muted">
                  Ban Appeals provides a seamless ban appeals process integrated directly within
                  Discord, simplifying the workflow for staff and users alike. Collect, review, and
                  resolve appeals without ever leaving your server.
                </p>

                <div className="hero-actions">
                  <a href="/invite" target="_blank" rel="noopener noreferrer" className="button button-primary hero-cta">
                    Add to Discord
                  </a>
                  <a href="/support" target="_blank" rel="noopener noreferrer" className="button button-outline">
                    Join Support Server
                  </a>
                </div>

                <p className="hero-small muted">
                  Manage everything within Discord or via our intuitive web dashboard.
                </p>
              </div>

              <div className="hero-right">
                <div className="hero-mascot-wrap">
                  <div className="hero-mascot-glow" />
                  <img
                    src={mascot}
                    alt="Ban Appeals mascot"
                    className="hero-mascot"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="section features">
          <div className="container">
            <header className="section-heading reveal">
              <h2 className="section-title">Built for fair, fast appeals</h2>
              <p className="section-subtitle muted">
                Everything you need to keep appeals organized, transparent, and painless for both
                staff and users.
              </p>
            </header>

            {/* 1. View & respond in Discord */}
            <div className="feature-row reveal">
              <div className="feature-media">
                <img
                  src={home1}
                  alt="View and respond to appeals in Discord"
                  className="feature-image"
                />
              </div>
              <div className="feature-copy">
                <h3>View and respond to appeals all within Discord</h3>
                <p className="muted">
                  Every appeal appears in a dedicated channel. Staff can read the
                  full context, reply, and resolve cases without jumping between dashboards,
                  spreadsheets, or forms.
                </p>
                <ul className="feature-list">
                  <li>Automatic ban context provided per user</li>
                  <li>Clear status for open, approved, and denied appeals</li>
                  <li>Keep conversations where your team already lives: Discord</li>
                </ul>
                <a href="/invite" target="_blank" rel="noopener noreferrer" className="button button-outline feature-premium-cta">
                  Add to Discord
                </a>
              </div>
            </div>

            {/* 2. Custom questions */}
            <div className="feature-row feature-row-reverse reveal">
              <div className="feature-media">
                <img
                  src={home2}
                  alt="Custom appeal questions"
                  className="feature-image"
                />
              </div>
              <div className="feature-copy">
                <h3>Custom questions for your server</h3>
                <p className="muted">
                  Ask the questions that matter for your community. Define the information you need
                  up front so staff can make quick, confident decisions.
                </p>
                <ul className="feature-list">
                  <li>Create and reorder custom appeal questions</li>
                  <li>Allows 1-5 free response questions per appeal</li>
                  <li>Keep responses structured and easy to review</li>
                </ul>
                <a href="/invite" target="_blank" rel="noopener noreferrer" className="button button-outline feature-premium-cta">
                  Add to Discord
                </a>
              </div>
            </div>

            {/* 3. Custom messages */}
            <div className="feature-row reveal">
              <div className="feature-media">
                <img
                  src={home3}
                  alt="Custom messages"
                  className="feature-image"
                />
              </div>
              <div className="feature-copy">
                <h3>Custom messages and responses</h3>
                <p className="muted">
                  Match your server's tone and expectations. Customize the messages users see when
                  they join your server, are banned, and receive appeal decisions.
                </p>
                <ul className="feature-list">
                  <li>Tailored DM messages</li>
                  <li>Include tokens for dynamic content</li>
                  <li>Your server, your style</li>
                </ul>
                <a href="/invite" target="_blank" rel="noopener noreferrer" className="button button-outline feature-premium-cta">
                  Add to Discord
                </a>
              </div>
            </div>

            {/* 4. Premium perks */}
            {/*<div className="feature-row feature-row-reverse reveal">
              <div className="feature-media">
                <img
                  src="/images/feature-premium.png"
                  alt="Premium perks"
                  className="feature-image"
                />
              </div>
              <div className="feature-copy">
                <h3>Premium perks for busy teams</h3>
                <p className="muted">
                  Upgrade to Ban Appeals Premium for the smoothest experience possible—especially
                  for larger servers and moderation teams.
                </p>
                <ul className="feature-list">
                  <li>Priority performance and extra configuration options</li>
                  <li>Enhanced automation and rules for handling appeals</li>
                  <li>Exclusive quality-of-life features for staff & users</li>
                </ul>
                <a href="/premium" className="button button-outline feature-premium-cta">
                  Learn more
                </a>
              </div>
            </div>*/}
          </div>
        </section>

        {/* PREMIUM BAND / FINAL CTA */}
        <section className="section premium-band">
          <div className="container">
            <div className="premium-card reveal">
              <div className="premium-copy">
                <h2>Ready to streamline your appeals?</h2>
                <p className="muted">
                  Give your community a standardized, simplified way to request second chances and give your
                  staff tools they actually enjoy using.
                </p>
              </div>
              <div className="premium-actions">
                <a href="/invite" target="_blank" rel="noopener noreferrer" className="button button-primary">
                  Add to Discord
                </a>
                <a href="/support" target="_blank" rel="noopener noreferrer" className="button button-outline">
                  Join Support Server
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

    </div>
  );
}
